import { useState, useEffect } from "react";
import { Card, Button, message, Select, Form, Input } from "antd";
import { useDispatch } from "react-redux";
import PageBreadcrumb from "../../component/PageBreadcrumb";
import { GetAllCourse } from "../../redux/actions/CoursesAction";
import axios from "axios";

const { Option } = Select;

const BulkAICourseEnrollment = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [loader, setLoader] = useState(false);
    const [file, setFile] = useState(null);
    const [aiCourses, setAICourses] = useState([]);
    const [aiCoursesLoading, setAICoursesLoading] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState(null);

    // Fetch AI courses on component mount
    useEffect(() => {
        setAICoursesLoading(true);
        const onSuccess = (courses) => {
            const onlyAI = (courses || []).filter((c) => (c?.subject || c?.subjects) === "AI");
            setAICourses(onlyAI);
            setAICoursesLoading(false);
        };
        const onError = (err) => {
            message.error(err || "Failed to load AI courses");
            setAICoursesLoading(false);
        };
        dispatch(GetAllCourse(1, 100, onSuccess, onError, "Active"));
    }, [dispatch]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && !selectedFile.name.endsWith('.csv')) {
            message.error('Please select a CSV file');
            e.target.value = null;
            return;
        }
        setFile(selectedFile);
    };

    const handleUpload = async () => {
        if (!selectedCourseId) {
            message.error('Please select an AI course');
            return;
        }

        if (!file) {
            message.error('Please select a CSV file');
            return;
        }

        setLoader(true);

        try {
            const formData = new FormData();
            formData.append('csvFile', file);

            const response = await axios.post(
                `${process.env.REACT_APP_UTIL_URL}/api/report/${selectedCourseId}/bulk-enrollments`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            setLoader(false);
            message.success(response?.data?.message || 'Bulk enrollment successful!');
            
            // Reset form
            form.resetFields();
            setFile(null);
            setSelectedCourseId(null);
            // Reset file input
            const fileInput = document.querySelector('input[type="file"]');
            if (fileInput) fileInput.value = '';
            
        } catch (error) {
            setLoader(false);
            message.error(
                error?.response?.data?.message || 
                error?.message || 
                'Bulk enrollment failed'
            );
        }
    };

    return (
        <div>
            <PageBreadcrumb route={["dashboard", "Bulk AI Course Enrollment"]} />

            <Card 
                title="Bulk AI Course Enrollment" 
                style={{ marginBottom: "1rem" }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Select AI Course"
                        name="courseId"
                        rules={[{ required: true, message: 'Please select an AI course' }]}
                        tooltip="Choose the AI course to activate for multiple users"
                    >
                        <Select
                            placeholder={aiCoursesLoading ? "Loading courses..." : "Select an AI course"}
                            loading={aiCoursesLoading}
                            disabled={aiCoursesLoading}
                            onChange={(value) => setSelectedCourseId(value)}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {aiCourses.map((course) => (
                                <Option key={course._id} value={course._id}>
                                    {course.courseName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Upload CSV File"
                        name="csvFile"
                        rules={[{ required: true, message: 'Please upload a CSV file' }]}
                        tooltip="Upload a CSV file containing learner phone numbers"
                    >
                        <Input 
                            type="file" 
                            accept=".csv"
                            onChange={handleFileChange} 
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button 
                            type="primary" 
                            onClick={handleUpload} 
                            loading={loader}
                            disabled={!selectedCourseId || !file}
                        >
                            {loader ? "Processing..." : "Enroll Users"}
                        </Button>
                    </Form.Item>
                </Form>

                <div style={{ marginTop: "1rem", padding: "1rem", background: "#f5f5f5", borderRadius: "4px" }}>
                    <h4>Instructions:</h4>
                    <ul style={{ marginBottom: 0 }}>
                        <li>Select an AI course from the dropdown</li>
                        <li>Upload a CSV file with learner phone numbers</li>
                        <li>The CSV must have a column named <strong>phoneNumber</strong></li>
                        <li>Example format:
                            <pre style={{ marginTop: "0.5rem", padding: "0.5rem", background: "white" }}>
phoneNumber
9876543210
9876543211
9876543212
                            </pre>
                        </li>
                        <li>All users in the CSV will be enrolled in the selected AI course</li>
                    </ul>
                </div>
            </Card>
        </div>
    );
};

export default BulkAICourseEnrollment;
